/**
 * Фильтры задачника, живущие в URL. Что считается фильтром, как его
 * проверить и во что он превращается в запросе — в @taskflow/client-core
 * (tasks/filters.ts), одно с приложением; здесь только хранение в адресе.
 *
 * §16.4 и §3.5: значимые фильтры хранятся в адресе, чтобы deep link
 * восстанавливал экран после обновления страницы и работал при пересылке
 * ссылки коллеге. Хранить их в состоянии компонента — значит терять их
 * при каждой перезагрузке.
 *
 * §16.3: произвольный период «с — по» валидируется здесь: «с» позже «до» —
 * пустая выборка, о которой пользователь должен узнать сразу.
 */

import {
  countActiveFilters,
  type FilterValidation,
  isValidIsoDate,
  parseTaskState,
  type TaskFilterState,
  validateFilters,
} from "@taskflow/client-core/tasks";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** Короткие имена параметров: адрес должен оставаться читаемым. */
const PARAM = {
  search: "q",
  assigneeIds: "assignee",
  priorityIds: "priority",
  typeIds: "prio_type",
  tagIds: "tag",
  dueFrom: "from",
  dueTo: "to",
  overdueOnly: "overdue",
  mineOnly: "mine",
  myToday: "today",
  state: "state",
} as const;

export function useTaskFilters(): {
  filters: TaskFilterState;
  validation: FilterValidation;
  activeCount: number;
  setFilter: <K extends keyof TaskFilterState>(key: K, value: TaskFilterState[K]) => void;
  setFilters: (patch: Partial<TaskFilterState>) => void;
  reset: () => void;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<TaskFilterState>(() => {
    const date = (key: string): string | null => {
      const value = params.get(key);
      // Мусор в адресе не должен ломать экран — просто игнорируется.
      return value !== null && isValidIsoDate(value) ? value : null;
    };

    return {
      search: params.get(PARAM.search) ?? "",
      assigneeIds: params.getAll(PARAM.assigneeIds),
      priorityIds: params.getAll(PARAM.priorityIds),
      typeIds: params.getAll(PARAM.typeIds),
      tagIds: params.getAll(PARAM.tagIds),
      dueFrom: date(PARAM.dueFrom),
      dueTo: date(PARAM.dueTo),
      overdueOnly: params.get(PARAM.overdueOnly) === "1",
      mineOnly: params.get(PARAM.mineOnly) === "1",
      myToday: params.get(PARAM.myToday) === "1",
      state: parseTaskState(params.get(PARAM.state)),
    };
  }, [params]);

  /**
   * Несколько фильтров — одним обновлением адреса.
   *
   * Функциональный setSearchParams получает prev из текущего адреса, а не
   * из очереди обновлений: два вызова подряд затирают друг друга. Так
   * «Применить» в фильтре срока записывало только «по», а «с» терялось.
   */
  const setFilters = useCallback(
    (patch: Partial<TaskFilterState>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of Object.keys(patch) as (keyof TaskFilterState)[]) {
            const name = PARAM[key];
            next.delete(name);

            // Значение приводится к общему типу до разбора: при обобщённом
            // ключе сужение через Array.isArray даёт any[].
            const raw: string[] | string | boolean | null | undefined = patch[key];
            if (Array.isArray(raw)) {
              for (const item of raw) next.append(name, item);
            } else if (typeof raw === "boolean") {
              if (raw) next.set(name, "1");
            } else if (typeof raw === "string" && raw.length > 0) {
              next.set(name, raw);
            }
          }
          return next;
        },
        // replace: фильтрация не должна засорять историю браузера —
        // кнопка «назад» обязана вести на предыдущий экран, а не отменять
        // последнюю галочку.
        { replace: true },
      );
    },
    [setParams],
  );

  const setFilter = useCallback(
    <K extends keyof TaskFilterState>(key: K, value: TaskFilterState[K]) => {
      setFilters({ [key]: value });
    },
    [setFilters],
  );

  const reset = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const name of Object.values(PARAM)) next.delete(name);
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  const activeCount = useMemo(() => countActiveFilters(filters), [filters]);
  const validation: FilterValidation = validateFilters(filters);

  return { filters, validation, activeCount, setFilter, setFilters, reset };
}
